import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { FormatData } from '@classes/format-data';
import { Order, LogElement } from '@models/order';
import { OrderService } from '@services/order.service';
import { PatientService } from '@services/patient.service';
import { ProductService } from '@services/product.service';

export class ReturnJob {
  static nextRtInvoice(originalInvoice: string, orders: Order[]): string {
    const root = FormatData.invoiceFamilyRoot(originalInvoice);
    if (!root) {
      return '';
    }
    const escaped = root.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`^${escaped}-RT(\\d+)?$`, 'i');
    let max = 0;
    (orders || []).forEach((order) => {
      const invoice = String(order?.invoiceNumber || '').trim();
      const match = invoice.match(pattern);
      if (!match) {
        return;
      }
      const n = match[1] ? parseInt(match[1], 10) : 1;
      if (!isNaN(n) && n > max) {
        max = n;
      }
    });
    if (max === 0) {
      return `${root}-RT`;
    }
    return `${root}-RT${max + 1}`;
  }

  static eligibleAmount(order: any): number {
    const retail = Number(order?.products?.[0]?.retail);
    if (!isNaN(retail) && retail > 0) {
      return retail;
    }
    return 0;
  }

  static restockingFeeAmount(eligible: number, percent: number): number {
    const fee = (Number(eligible) || 0) * (Number(percent) || 0) / 100;
    return Math.round(fee * 100) / 100;
  }

  static buildLinkedReturnOrder(
    original: any,
    originalInvoice: string,
    invoiceNumber: string,
    financial: {
      credit: number;
      restockingFee: number;
      netCredit: number;
      restockingFeeApplied: boolean;
    },
    context: {
      patientId: string;
      userId: string;
      store: any;
      initials: string;
    }
  ): Order {
    const clone = JSON.parse(JSON.stringify(original || {}));
    clone._id = '';
    clone.invoiceNumber = invoiceNumber;
    clone.linkedInvoice = originalInvoice;
    clone.returnCredit = financial.netCredit || 0;
    clone.restockingFee = financial.restockingFee || 0;
    clone.restockingFeeWaived = !financial.restockingFeeApplied;
    clone.exchangeCredit = 0;
    clone.frameOnly = false;
    clone.frameOnlyTaxCredit = 0;
    clone.redoReason = '';
    clone.redoPriority = false;
    clone.redoPaidCredit = 0;
    clone.status = 'RETURNED';
    clone.todo = 'SAVE';
    clone.jobAction = 'RETURN';
    clone.jobActions = ['RETURN'];
    clone.lensStatus = '';
    clone.rxStatus = '';
    clone.eta = '';
    clone.archived = false;
    clone.framesRemoved = [];
    clone.tax = 0;
    clone.shipping = 0;
    clone.total = 0;
    clone.vendorOrder = null;
    clone.fulfillment = 'PICKUP';
    clone.date = new Date();
    clone.dateAdded = undefined;
    clone.dateUpdated = undefined;
    clone.patient = context.patientId;
    clone.user = context.userId;
    clone.store = context.store;
    delete clone.initials;

    const now = new Date();
    (clone.products || []).forEach((productElement: any) => {
      productElement._id = null;
      productElement.tax = 0;
      productElement.shipping = 0;
      productElement.destination = '';
      productElement.subtotal = 0;

      if (!productElement.frame) {
        productElement.frame = {};
      }
      productElement.frame.pof = false;
      productElement.frame.deposit = 0;
      productElement.frame.enclosed = false;
      productElement.frame.physicalStatus = '';
      productElement.frame.preOrderStatus = '';
      productElement.frame.activity = [
        {
          date: now,
          status: 'FRAME RETURNED TO INVENTORY',
          referenceNumber: '',
          carrier: '',
          trackingNumber: '',
          labVendor: '',
        },
      ];
      productElement.frame.activityStatus = 'FRAME RETURNED TO INVENTORY';
      productElement.frame.dateReceived = '';
      productElement.frame.followUpDate = '';
      productElement.frame.followUpStatus = '';
      productElement.frame.referenceNumber = '';
      productElement.frame.labVendor = '';
      productElement.frame.carrier = '';
      productElement.frame.trackingNumber = '';
      productElement.frame.shipDate = '';
      productElement.frame.repairItems = [];
      productElement.frame.orderDate = '';
      productElement.frame.failedInspection = false;

      if (productElement.lens) {
        productElement.lens.labReferenceNumber = '';
        productElement.lens.POLLabReferenceNumber = '';
        productElement.lens.date = '';
        productElement.lens.failedInspection = '';
      }
    });

    const created = new LogElement();
    created.initials = context.initials;
    created.user = context.userId;
    created.item = 'JOB CREATED';
    created.notes = '';
    created.date = Date.now().toString();

    const returnLog = new LogElement();
    returnLog.initials = context.initials;
    returnLog.user = context.userId;
    returnLog.item = originalInvoice
      ? `RETURN FROM ${originalInvoice}`
      : 'RETURN';
    const feeLabel = financial.restockingFeeApplied
      ? 'Restocking Fee Applied'
      : 'Restocking Fee Waived';
    returnLog.notes = `Return Processed — ${feeLabel} — Customer Credit $${FormatData.formatCurrency(
      financial.netCredit
    )}`;
    returnLog.date = Date.now().toString();
    clone.log = [created, returnLog];

    return clone as Order;
  }

  static create$(
    original: any,
    originalInvoice: string,
    existingOrders: Order[],
    financial: {
      credit: number;
      restockingFee: number;
      netCredit: number;
      restockingFeeApplied: boolean;
    },
    context: {
      patientId: string;
      userId: string;
      store: any;
      initials: string;
    },
    services: {
      orderService: OrderService;
      productService: ProductService;
      patientService: PatientService;
    }
  ): Observable<Order> {
    const invoiceNumber = ReturnJob.nextRtInvoice(
      originalInvoice,
      existingOrders
    );
    const newOrder = ReturnJob.buildLinkedReturnOrder(
      original,
      originalInvoice,
      invoiceNumber,
      financial,
      context
    );
    const product = original?.products?.[0]?.product;
    const quantity = parseInt(original?.products?.[0]?.quantity, 10) || 1;
    const wasPof = !!original?.products?.[0]?.frame?.pof;

    return services.orderService.addOrder(newOrder).pipe(
      switchMap((createdOrders: Order[]) => {
        const created = Array.isArray(createdOrders)
          ? createdOrders[0]
          : createdOrders;
        const restoreInventory$ =
          !wasPof && product?._id && product._id !== 'NEW'
            ? services.productService
                .incrementProductInventoryQuantity(product, Math.abs(quantity))
                .pipe(catchError(() => of(null)))
            : of(null);
        const credit$ =
          financial.netCredit > 0
            ? services.patientService
                .incrementStoreCredit(context.patientId, financial.netCredit)
                .pipe(catchError(() => of(null)))
            : of(null);
        return restoreInventory$.pipe(
          switchMap(() => credit$),
          map(() => created)
        );
      })
    );
  }
}
